import React, { useEffect, useMemo, useState } from 'react';
import ReactFlow, { Controls, Background } from 'react-flow-renderer';
import 'react-flow-renderer/dist/style.css';
import { Box, Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { shouldUseCallOffInTraceabilityGraph } from '../../../../utils/tenantFeatures';
import { formatDurationMinutes } from '../../../../utils/formatDurationMinutes';
import {
  buildPerformanceByOriginProcess,
  buildRowIndexByProcessId,
  getCriticalPathMinutes,
  getNodeDurationMinutes,
  getOpenProcessStatus,
  SEMAPHORE_COLORS,
  toFiniteNumber,
  toValidDate,
  getVisualLevel,
} from '../processTraceabilityGraph.utils';

const buildNodeUrl = (node) => {
  const { processId, processType, documentNumber } = node;

  if (processType === 'Despacho') {
    return `/dispatches?dispatchNumber=${documentNumber}`;
  }

  if (processType === 'Recepción') {
    return `/receipts?lotNumber=${documentNumber}`;
  }

  return `/process/${processId}`;
};

const getDisplayProcessTypeLabel = (processType, t) => {
  if (processType === 'Despacho') return t('lotTraceability.dispatch');
  if (processType === 'Recepción') return t('lotTraceability.receipt');
  return processType;
};

export default function ProcessTraceabilityChart(props) {
  const { traceabilityGraph, pointOnClick } = props;
  const { t } = useTranslation();
  const [nowMs, setNowMs] = useState(Date.now());
  const useCallOffReference = shouldUseCallOffInTraceabilityGraph();

  const nodesSource = Array.isArray(traceabilityGraph?.nodes) ? traceabilityGraph.nodes : [];
  const edgesSource = Array.isArray(traceabilityGraph?.edges) ? traceabilityGraph.edges : [];

  if (!nodesSource.length) {
    return (
      <Box p={2}>
        <Typography variant="body2" color="textSecondary">
          Sin datos de trazabilidad para mostrar.
        </Typography>
      </Box>
    );
  }

  const hasOpenProcesses = useMemo(
    () => nodesSource.some((node) => !node?.closedAt && toValidDate(node?.openedAt)),
    [nodesSource],
  );

  useEffect(() => {
    if (!hasOpenProcesses) return undefined;

    const intervalId = setInterval(() => {
      setNowMs(Date.now());
    }, 60000);

    return () => clearInterval(intervalId);
  }, [hasOpenProcesses]);

  const rowIndexByProcessId = buildRowIndexByProcessId(nodesSource, edgesSource);
  const performanceByProcessId = buildPerformanceByOriginProcess(edgesSource);
  const totalProcessMinutes = useMemo(
    () => getCriticalPathMinutes(nodesSource, edgesSource, nowMs),
    [nodesSource, edgesSource, nowMs],
  );

  const nodes = nodesSource.map((node) => {
    const { id, processId, processType, documentNumber, fruitNames, callOff, returned, current } =
      node;

    const displayProcessType = getDisplayProcessTypeLabel(processType, t);

    const primaryReference =
      useCallOffReference && processType === 'Recepción'
        ? callOff || documentNumber || processId
        : documentNumber || processId;

    let label = `${primaryReference} ${displayProcessType}`;
    if (useCallOffReference && processType !== 'Recepción' && callOff) {
      label += ` (${callOff})`;
    }
    if (fruitNames) label += ` (${fruitNames})`;
    if (returned) label += t('lotTraceability.returned');
    const nodeDurationMinutes = getNodeDurationMinutes(node, nowMs);
    if (nodeDurationMinutes !== null) {
      label += ` - ${formatDurationMinutes(nodeDurationMinutes, { shortUnderHour: true })}`;
    }

    const rowIndex = rowIndexByProcessId.get(processId) || 0;
    const visualLevel = getVisualLevel(node);
    const performanceStatus =
      performanceByProcessId.get(processId) || getOpenProcessStatus(node, nowMs);
    const hasThresholds =
      toFiniteNumber(node?.lowTimeThreshold) !== null ||
      toFiniteNumber(node?.highTimeThreshold) !== null;

    let nodeColors = {
      background: processType === 'Recepción' ? '#FFFFFF' : '#ECEFF1',
      border: processType === 'Recepción' ? '#8BC34A' : '#90A4AE',
      color: processType === 'Recepción' ? '#2979FF' : '#263238',
    };

    if (hasThresholds && performanceStatus && SEMAPHORE_COLORS[performanceStatus]) {
      nodeColors = SEMAPHORE_COLORS[performanceStatus];
    }

    return {
      id,
      position: {
        x: visualLevel * 340,
        y: rowIndex * 100,
      },
      data: {
        label,
        url: buildNodeUrl(node),
      },
      style: {
        borderRadius: 8,
        border: `2px solid ${current ? 'red' : nodeColors.border}`,
        background: nodeColors.background,
        color: nodeColors.color,
        padding: '8px 10px',
        fontSize: 12,
        fontWeight: 600,
      },
      sourcePosition: 'right',
      targetPosition: 'left',
    };
  });

  const edges = edgesSource.map((edge) => ({
    id: edge.id || `edge-${edge.fromProcessId}-${edge.toProcessId}`,
    source: `process-${edge.fromProcessId}`,
    target: `process-${edge.toProcessId}`,
    type: 'straight',
    arrowHeadType: 'arrowclosed',
    animated: false,
    style: { stroke: '#358A7C', strokeWidth: 1.4 },
  }));

  return (
    <Box>
      <Box mb={1}>
        <Typography variant="subtitle2" color="textSecondary">
          {t('lotTraceability.totalProcessTime', {
            time: formatDurationMinutes(totalProcessMinutes, { shortUnderHour: true }),
          })}
        </Typography>
      </Box>
      <Box style={{ height: '70vh', width: '100%', minHeight: 500 }}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          fitView
          fitViewOptions={{ padding: 0.2 }}
          onNodeClick={(_event, clickedNode) => pointOnClick({ url: clickedNode?.data?.url })}>
          <Controls />
          <Background gap={20} size={1} />
        </ReactFlow>
      </Box>
    </Box>
  );
}
